import React, { useState, useCallback, useMemo } from "react";
import {
    GridTable,
    GridTableHeader,
} from "../../general/Tables/GridTable/GridTable";
import { SortableTreeList } from "../../general/SortableTreeList/SortableTreeList";
import { useCategoriesTree } from "../../../hooks/useCategoriesTree";
import { FiltersTypes } from "../../../common/types/filters/general";
import { useEntities } from "../../../hooks/useEntities";
import { CategoryFlattenedItem } from "./utils";
import styles from "./Categories.module.scss";
import { CategoriesHeader } from "./children/CategoriesHeader";
import { CategoryItem } from "./children/CategoryItem";
import { useCustomCategoryFlattenedItems } from "./useCustomCategoryFlattenedItems";
import { useCategoryStatistics } from "./useCategoryStatistics";
import { CategoriesStats } from "./CategoriesStats";

interface Props {
    closeSettingsModal?: (disableAnimation?: boolean) => void;
}

export const Categories: React.FC<Props> = () => {
    const categories = useCategoriesTree();
    const businessEntities = useEntities({ onlyBusiness: true });

    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
        new Set(),
    );

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<
        Pick<FiltersTypes, "end" | "start" | "entityIds" | "category">
    >({ entityIds: businessEntities.map((entity) => entity.id) });

    const stats = useCategoryStatistics(filters);

    const statsMap = useMemo(
        () => new Map((stats.data?.body ?? []).map((stat) => [stat.id, stat])),
        [stats.data?.body],
    );

    const flattenedItems = useCustomCategoryFlattenedItems(search, filters);

    const total = useMemo(
        () =>
            flattenedItems
                .map((item) =>
                    item.meta.isHidden
                        ? 0
                        : statsMap.get(item.meta.id)?.count ?? 0,
                )
                .reduce((acc, stat) => acc + (stat ?? 0), 0),
        [flattenedItems, statsMap],
    );

    const renderItemCallback = useCallback(
        (category: CategoryFlattenedItem) => (
            <CategoryItem
                item={category}
                collapsedCategories={collapsedCategories}
                setCollapsedCategories={setCollapsedCategories}
                percentage={
                    ((statsMap.get(category.meta.id)?.count ?? 0) / total) * 100
                }
                groupPercentage={
                    ((statsMap.get(category.meta.id)?.groupCount ?? 0) /
                        total) *
                    100
                }
                transactionsCount={statsMap.get(category.meta.id)?.count ?? 0}
                groupTransactionsCount={
                    statsMap.get(category.meta.id)?.groupCount ?? 0
                }
                filters={filters}
            />
        ),
        [collapsedCategories, setCollapsedCategories, filters, statsMap, total],
    );

    const getItemId = useCallback((c: CategoryFlattenedItem) => c.id, []);

    const handleToggleCategory = useCallback(
        (val: string) => {
            const parentCategoryId =
                flattenedItems.find((item) => item.id === val)?.meta.category
                    ?.parentCategoryId ?? val;

            if (filters.category?.includes(parentCategoryId)) {
                setFilters((prev) => ({ ...prev, category: undefined }));
            } else {
                setFilters((prev) => ({
                    ...prev,
                    category: [parentCategoryId],
                }));
            }
        },
        [flattenedItems, filters, setFilters],
    );

    const flattenedItemsWithValue = useMemo(
        () => flattenedItems.filter((item) => !item.meta.isHidden),
        [flattenedItems],
    );

    return (
        <main>
            <CategoriesHeader
                filters={filters}
                onFiltersChange={(newFilters) =>
                    setFilters((prev) => ({ ...prev, ...newFilters }))
                }
                collapsedCategories={collapsedCategories}
                setCollapsedCategories={setCollapsedCategories}
                categories={categories}
                search={search}
                setSearch={setSearch}
            />

            {flattenedItemsWithValue.length > 0 && total > 0 && (
                <CategoriesStats
                    statsMap={statsMap}
                    stats={stats.data?.body ?? []}
                    total={total}
                    categories={flattenedItems}
                    toggleCategory={handleToggleCategory}
                />
            )}

            <GridTable className={styles.categoriesTable}>
                <GridTableHeader
                    columnBorders={false}
                    columns={[
                        { key: "name", label: "Name" },
                        { key: "description", label: "Description" },
                        { key: "value", label: "%" },
                        { key: "transactions", label: "Transactions" },
                        { key: "actions", label: "" },
                    ]}
                    className={styles.headerRow}
                />

                <SortableTreeList
                    items={flattenedItemsWithValue}
                    getItemId={getItemId as any}
                    listId="categories"
                    renderItem={renderItemCallback as any}
                    emptyStateText="No categories found"
                />
            </GridTable>
        </main>
    );
};
